import { KEEP_PLACE } from "@/lib/search";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { phoneKey, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { Inbox, MessageSquare } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan, useFeature } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { FileNumberDialog } from "./-components/file-number-dialog";
import { SmsComposer } from "./-components/sms-composer";
import { SmsEmpty } from "./-components/sms-empty";
import { SmsInbox } from "./-components/sms-inbox";
import { smsListSearch, smsListView, type SmsListSearch } from "./-components/sms-list-search";
import {
  fileNumberProps,
  smsInboxActionProps,
  type FilingTarget,
} from "./-components/sms-inbox-actions";
import { useSmsThreadActions } from "./-components/use-sms-thread-actions";
import { metaFor } from "./-components/messages-meta";
import { buildThreads, needsReply, type SmsThread } from "./-components/sms-threads";

type MessagesSearch = SmsListSearch & { facility?: string; thread?: string };

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/messages")({
  /* `?facility=&thread=` is the link a staff member is texted when Sarah hands
     a conversation to them: it opens on exactly that thread. */
  validateSearch: (search: Record<string, unknown>): MessagesSearch => ({
    ...smsListSearch(search),
    ...(typeof search.facility === "string" && search.facility
      ? { facility: search.facility }
      : {}),
    ...(typeof search.thread === "string" && search.thread ? { thread: search.thread } : {}),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "sarah-messages"),
  component: FrontDeskMessages,
});

/** The front desk's text conversations, shown even without the Hiring plan. Every conversation
 * shows, labelled by kind, since one number serves candidates and callers. Writing first needs
 * `frontdesk:manage`, and the API refuses the write too. */
function FrontDeskMessages() {
  const { facilityId: switcherFacilityId } = useFacility();
  const link = Route.useSearch();
  const navigate = Route.useNavigate();
  const patch = (next: Partial<MessagesSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const { text: term, change: setTerm } = useSearchParamText(link.q, (q) => patch({ q }));
  const list = smsListView(link, term, setTerm, patch);
  // The linked thread's own building wins, so the link works whatever the switcher says.
  const facilityId = link.facility ?? switcherFacilityId;
  // Names are a label, not a permission: without them threads are still
  // separated per building, they simply carry no chip.
  const canSeeFacilities = useCan("facility:read");
  const canCompose = useCan("frontdesk:manage");
  // Naming a caller reads the Directory; filing one writes to it.
  const canReadDirectory = useCan("directory:read");
  const canFileNumbers = useCan("directory:manage");
  const directoryEnabled = useFeature("directory");
  const [composing, setComposing] = useState(false);

  /* Who is answering each thread. Polled: a handoff's wait and a colleague
     taking over both change it under you. */
  const agents = useQuery({
    ...orpc.frontDesk.messages.threadAgents.queryOptions({ input: { facilityId } }),
    refetchInterval: 10_000,
  });
  const sarahLive = (agents.data ?? []).some(
    (agent) =>
      agent.mode === "sarah" || agent.mode === "shelby" || agent.mode === "handoff_pending",
  );
  /* The conversations poll too: Sarah's replies land without anybody here
     sending, so the list looks for itself — every few seconds while she is on a
     thread, at a walk otherwise, and whenever the window comes back. */
  const messages = useQuery({
    ...orpc.frontDesk.messages.list.queryOptions({ input: { facilityId } }),
    refetchInterval: sarahLive ? 4_000 : 15_000,
    refetchOnWindowFocus: true,
  });
  const states = useQuery(
    orpc.frontDesk.messages.threadStates.queryOptions({ input: { facilityId } }),
  );
  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: true } }),
    enabled: canSeeFacilities,
  });

  const facilityNames = useMemo(
    () => new Map((facilities.data ?? []).map((facility) => [facility.id, facility.name])),
    [facilities.data],
  );
  /* Which buildings a new message may be written from. Without the directory
     (no `facility:read`) there is nothing to choose between, so the composer
     writes from the building the switcher is on. */
  const composeFacilities = useMemo(
    () =>
      (facilities.data ?? []).length > 0
        ? (facilities.data ?? []).map((facility) => ({ id: facility.id, name: facility.name }))
        : facilityId === "all"
          ? []
          : [{ id: facilityId, name: "This community" }],
    [facilities.data, facilityId],
  );

  /* The Directory, keyed on the numbers it holds. A caller who is already a physician, a pharmacy,
   * a family member or a saved contact should read as that person here rather than as ten digits —
   * and the numbers it does NOT hold are the ones worth offering to file. */
  const phoneBook = useQuery({
    ...orpc.care.directories.search.queryOptions({
      input: { facilityId, channel: "phone" },
    }),
    enabled: canReadDirectory && directoryEnabled,
  });
  const nameByNumber = useMemo(() => {
    const index = new Map<string, string>();
    for (const entry of phoneBook.data ?? []) {
      const key = phoneKey(entry.phone);
      if (key && !index.has(key)) index.set(key, entry.name);
    }
    return index;
  }, [phoneBook.data]);

  /* Which conversations began on the community website. Thread rows carry the claimed visit;
   * messages carry only the reference code. `agents.data` keeps identity while unchanged so the
   * ten-second poll does not rebuild threads. */
  const websiteThreadIds = useMemo(
    () =>
      new Set(
        (agents.data ?? []).flatMap((agent) =>
          agent.website ? [`${agent.facilityId}\u0000${agent.threadKey}`] : [],
        ),
      ),
    [agents.data],
  );

  const threads = useMemo(() => {
    const built = buildThreads(
      messages.data ?? [],
      (id) => facilityNames.get(id) ?? null,
      states.data ?? [],
      websiteThreadIds,
    );
    if (nameByNumber.size === 0) return built;
    /* A candidate's own name wins: Hiring knows who applied, and the
       Directory's row for the same number is the building's copy of it. */
    return built.map((thread) =>
      thread.name ? thread : { ...thread, name: nameByNumber.get(phoneKey(thread.key)) ?? null },
    );
  }, [messages.data, states.data, facilityNames, nameByNumber, websiteThreadIds]);

  const { save, refreshStates, markRead, markAll, toggleStar, setHandled, agentAction } =
    useSmsThreadActions(threads);

  /* A number nothing in the Directory names, on a thread that is not a
     candidate's: worth offering to file where the next text — and the next
     fax, the next email — will find it. */
  const [filing, setFiling] = useState<FilingTarget | null>(null);
  const unfiled = (thread: SmsThread) =>
    canFileNumbers && !thread.applicantId && !nameByNumber.has(phoneKey(thread.key));

  const unread = threads.reduce((total, thread) => total + thread.unread, 0);
  const oneBuilding = new Set(threads.map((thread) => thread.facilityId)).size === 1;
  /* The configured texting line comes from the connection table. A failed lookup names NO line:
   * traffic is the only other source and it names the wrong one (deleted connections, numbers
   * pointed at this webhook). */
  const configured = useQuery({
    ...orpc.communications.lines.get.queryOptions({ input: { facilityId } }),
    enabled: facilityId !== "all",
  });
  const lines =
    facilityId === "all" || configured.isError || !configured.data?.sms?.number
      ? []
      : [configured.data.sms.number];

  const header = (
    <PageHeader
      title="Front desk texts"
      count={messages.data && threads.length > 0 ? threads.length : undefined}
      meta={metaFor({
        loading: messages.isPending,
        failed: messages.isError,
        threads,
        unread,
        waiting: threads.filter(needsReply).length,
        named: oneBuilding ? (threads[0] ?? null) : null,
        lines,
      })}
      actions={
        <>
          {unread > 0 ? (
            <Button variant="outline" disabled={save.isPending} onClick={() => void markAll()}>
              Mark all read
            </Button>
          ) : null}
          {canCompose ? <Button onClick={() => setComposing(true)}>New message</Button> : null}
        </>
      }
    />
  );
  /* With the inbox up, the heading moves into it: from `xl` it heads the third
     pane, so the conversations get the height it held. */
  const inbox = !messages.isPending && !messages.isError && threads.length > 0;

  return (
    <>
      {inbox ? null : header}

      {/* Loading, then failure, then empty: a failed load must never render as
          "no texts yet". */}
      {messages.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : messages.isError ? (
        <EmptyState
          title="Couldn't load the inbox"
          description={errorWords(messages.error)}
          action={
            <Button variant="outline" onClick={() => void messages.refetch()}>
              Try again
            </Button>
          }
        />
      ) : threads.length === 0 ? (
        /* "No texts yet" would be a lie on a line that has sent forty automated
           texts and had none answered, which opens no conversation. */
        (messages.data ?? []).length > 0 ? (
          <SmsEmpty
            icon={<Inbox />}
            title="Nothing to reply to yet"
            description="Texts sent after a call show here once somebody writes back. Until then they are on the call record."
          />
        ) : (
          <SmsEmpty
            icon={<MessageSquare />}
            title="No texts yet"
            description="Texts to and from this community's number show here, one conversation each."
          />
        )
      ) : (
        <SmsInbox
          head={header}
          summary={header}
          list={list}
          threads={threads}
          onOpenThread={markRead}
          onToggleStar={toggleStar}
          agents={agents.data ?? []}
          initialThreadId={
            link.facility && link.thread ? `${link.facility}\u0000${link.thread}` : null
          }
          {...smsInboxActionProps(agentAction, setHandled)}
          {...fileNumberProps(canFileNumbers, setFiling, unfiled)}
        />
      )}

      {/* A new number, filed where the next text will find it. */}
      <FileNumberDialog filing={filing} onClose={() => setFiling(null)} />

      {composing ? (
        <SmsComposer
          facilities={composeFacilities}
          defaultFacilityId={facilityId}
          threads={threads}
          onClose={() => setComposing(false)}
          onSent={() => void refreshStates(true)}
        />
      ) : null}
    </>
  );
}
